// ---------------------------------------------------------------------------
// Handing the queue to the server, and knowing when to try.
//
// The rules this file follows, in order of how much they matter:
//
//   1. A SALE IS NEVER DELETED UNTIL THE SERVER CONFIRMS IT HOLDS IT. Not on
//      a timeout, not on an unexpected response, not to keep the queue tidy.
//      The server answers per sale with `synced` or `duplicate`; only those
//      ids are forgotten.
//
//   2. A FAILED SALE STAYS, WITH ITS REASON. Something the server refuses -
//      a deleted product, a shift that no longer exists - is a decision for a
//      person, not for a retry loop. It is shown, and it waits.
//
//   3. ONE SYNC AT A TIME. The reconnect event, the interval and the manual
//      button can all fire within a second of each other. Overlapping runs
//      would send the same sales twice, which the server would handle
//      correctly and which would still be a waste of a shop's connection.
//
//   4. RETRY SLOWLY. A till that cannot reach the server should not spend the
//      connection asking every second. Backs off to a minute.
//
// The keystone is that none of this has to be perfect, because the server
// rejects duplicates on an idempotency key the device generated when the sale
// was rung up. This file can be interrupted at any point and repeated safely.
// ---------------------------------------------------------------------------

import { api, ApiError } from './apiClient';
import {
  clearSaleFailure,
  countHeldOps,
  countPendingSales,
  queueHeldOp,
  deviceId,
  forgetSales,
  listHeldOps,
  listPendingSales,
  forgetHeldOps,
  markSaleFailed,
  updateHeldOp,
  offlineStorageAvailable,
  queueSale,
  readCatalogue,
  saveCatalogue,
  saveReceiptReservations,
  type CachedCatalogue,
  type PendingHeldOp,
  type PendingSale,
} from './offlineDb';
import { useOfflineStore } from '../stores/offlineStore';
import type { PrintPlan } from '../hooks/useSalePrinting';

/// How many sales go in one request. The server caps this at 50.
const BATCH_SIZE = 25;

/// How many receipt numbers the till keeps in hand for selling offline. An
/// evening's outage, not a week's: every unused number is a gap in the day's
/// sequence when the till is retired.
export const RECEIPT_POOL = 10;

interface SyncItemResult {
  offlineId: string;
  outcome: 'synced' | 'duplicate' | 'failed';
  saleId?: string;
  receiptNumber?: string;
  reason?: string;
  intoClosedShift?: boolean;
  /// The bench tickets the recorded sale wrote, to print now.
  printPlan?: PrintPlan;
}

interface SyncResponse {
  result: {
    synced: number;
    duplicates: number;
    failed: number;
    results: SyncItemResult[];
  };
}

/// Guards rule 3. A module-level flag rather than store state, because the
/// check has to be synchronous and cannot wait for React to re-render.
let running = false;
/// The held-cart sweep's own guard. Separate from `running`: one cart in
/// conflict must never hold up somebody's money.
let heldRunning = false;
let timer: ReturnType<typeof setInterval> | null = null;
let healthTimer: ReturnType<typeof setInterval> | null = null;
let listenersAttached = false;

/// Consecutive failed health probes. See `probeHealth` for why this exists.
let missedProbes = 0;

/// The probe currently in flight, if any.
///
/// Two probes at once is not a hypothetical: the twenty-second timer, the
/// `online` event and a screen mounting can all ask within the same second,
/// and the four-second timeout means the first is still open when the second
/// starts. Sharing the in-flight promise means one request, one answer, and
/// no chance of the pair landing out of order.
let inFlight: Promise<boolean> | null = null;

/// Which probe is the newest. Answers from an older one are discarded.
///
/// THE RACE THIS CLOSES. A probe that will fail after its four-second timeout
/// is started; a second later the server comes back and a probe started after
/// it succeeds immediately. Without this counter the older answer lands last
/// and writes `serverReachable: false` over a fresher `true` - the badge then
/// says Offline while the till is demonstrably talking to the server, and
/// stays wrong until the next sweep twenty seconds later.
let probeSequence = 0;
let newestApplied = 0;

/// Take the next ticket. Whoever holds the highest one owns the answer.
function claimTicket(): number {
  return ++probeSequence;
}

/// Write reachability, unless something newer has already been written.
///
/// EVERY write goes through here - the probe's answer and the browser's
/// `offline` event alike. The event used to write directly, which is the
/// reachable form of this race: the interface goes down, the event says so,
/// and a probe that was already in flight then answers "reachable" and
/// resurrects a connection that no longer exists. The badge then reads Online
/// with no network at all, until the next sweep twenty seconds later.
function applyReachable(ticket: number, reachable: boolean | null): void {
  if (ticket < newestApplied) return;
  newestApplied = ticket;
  useOfflineStore.getState().setServerReachable(reachable);
}

/// Push the current queue into the store so the screen can show it.
export async function refreshQueue(): Promise<void> {
  if (!offlineStorageAvailable()) return;

  try {
    const pending = await listPendingSales();
    useOfflineStore.getState().setPending(pending);
  } catch {
    // A browser that cannot read its own store is a browser that cannot sell
    // offline. It can still sell online, so this is not fatal.
  }
}

/// Put a completed sale into the queue and update the screen.
export async function enqueueSale(sale: PendingSale): Promise<void> {
  await queueSale(sale);
  await refreshQueue();
}

/// Hand everything waiting to the server.
///
/// Returns what happened, or null when there was nothing to do or a sync was
/// already in progress.
export async function syncNow(options: { manual?: boolean } = {}): Promise<{
  synced: number;
  duplicates: number;
  failed: number;
} | null> {
  if (!offlineStorageAvailable()) return null;
  if (running) return null;

  const store = useOfflineStore.getState();

  // A manual press tries even when the browser thinks it is offline: the
  // cashier can see the Wi-Fi symbol and we cannot, and being wrong costs one
  // failed request. And a server that has ANSWERED since the browser's
  // opinion is reachable whatever that opinion was - the shop's server sits
  // on the same machine, and Windows' view of the internet says nothing
  // about it.
  if (!options.manual && !store.browserOnline && store.serverReachable !== true) return null;

  const queue = await listPendingSales();

  // Sales the server has already refused are not retried automatically. A
  // person clears the failure, which is what the retry button does.
  const sendable = queue.filter((sale) => sale.failedReason === undefined);
  if (sendable.length === 0) {
    await refreshQueue();
    return null;
  }

  running = true;
  store.setPhase('syncing');

  const totals = { synced: 0, duplicates: 0, failed: 0 };

  try {
    const label = await deviceId();

    for (let index = 0; index < sendable.length; index += BATCH_SIZE) {
      const batch = sendable.slice(index, index + BATCH_SIZE);

      const response = await api.post<SyncResponse>('/sales/sync', {
        deviceLabel: label,
        // The id the receipt numbers were reserved under: a queued sale that
        // printed under one is accepted only when this matches.
        deviceId: label,
        sales: batch.map((sale) => ({
          ...sale.payload,
          offlineCreatedAt: sale.createdAt,
          offlineShiftId: sale.shiftId,
        })),
      });

      const settled: string[] = [];
      const owedTickets: PrintPlan[] = [];

      for (const item of response.result.results) {
        if (item.outcome === 'synced' || item.outcome === 'duplicate') {
          // The server holds it. Only now is it safe to forget.
          settled.push(item.offlineId);
          if (item.printPlan) owedTickets.push(item.printPlan);
        } else {
          await markSaleFailed(item.offlineId, item.reason ?? 'The server refused this sale.');
        }
      }

      await forgetSales(settled);
      if (owedTickets.length > 0) useOfflineStore.getState().addTicketPlans(owedTickets);

      totals.synced += response.result.synced;
      totals.duplicates += response.result.duplicates;
      totals.failed += response.result.failed;
    }

    useOfflineStore.getState().setServerReachable(true);
    useOfflineStore.getState().setLastSync({ at: new Date().toISOString(), ...totals });
    useOfflineStore.getState().setPhase(totals.failed > 0 ? 'failed' : 'idle');

    // The numbers spent offline are gone; ask for the pool to be filled
    // while the connection is demonstrably good.
    void topUpReceiptReservations();

    return totals;
  } catch (error) {
    // The whole request failed - no connection, or the server is down. Nothing
    // is marked as failed, because nothing was refused; the queue is intact
    // and the next attempt will send it again.
    const message =
      error instanceof ApiError
        ? error.message
        : 'The server could not be reached. Nothing was lost; it will be sent again.';

    const state = useOfflineStore.getState();
    if (error instanceof ApiError && error.code === 'NETWORK_ERROR') {
      state.setServerReachable(false);
    }
    state.setLastError(message);
    state.setPhase('failed');

    return null;
  } finally {
    running = false;
    await refreshQueue();
  }
}

// ---------------------------------------------------------------------------
// Held carts
// ---------------------------------------------------------------------------

/// What the server says about one queued operation.
interface HeldSyncItemResult {
  opId: string;
  outcome: 'applied' | 'duplicate' | 'conflict' | 'failed';
  heldOrderId?: string;
  holdNumber?: string;
  reason?: string;
  serverVersion?: number;
  serverStatus?: string;
}

interface HeldSyncResponse {
  result: { results: HeldSyncItemResult[] };
}

/// Hand over what this till did to its held carts while it was away.
///
/// SEPARATE FROM THE SALES SWEEP, and it has to be. A sale is a fact that
/// happened and the server records it; a held cart is shared state that the
/// server is allowed to refuse. Mixing them would mean one cart in conflict
/// holding up somebody's money.
///
/// Returns what happened, or null when there was nothing to send.
export async function syncHeldOps(options: { manual?: boolean } = {}): Promise<{
  applied: number;
  duplicates: number;
  conflicts: number;
  failed: number;
} | null> {
  if (!offlineStorageAvailable()) return null;
  if (heldRunning) return null;

  const store = useOfflineStore.getState();
  if (!options.manual && !store.browserOnline && store.serverReachable !== true) return null;

  const queue = await listHeldOps();

  // A conflict waits for a person. A refusal waits for a person. Neither is
  // retried on its own, because both mean somebody has to look.
  const sendable = queue.filter((op) => !op.conflict && op.failedReason === undefined);
  if (sendable.length === 0) return null;

  heldRunning = true;
  const totals = { applied: 0, duplicates: 0, conflicts: 0, failed: 0 };

  try {
    const label = await deviceId();

    for (let index = 0; index < sendable.length; index += BATCH_SIZE) {
      const batch = sendable.slice(index, index + BATCH_SIZE);

      const response = await api.post<HeldSyncResponse>('/held-orders/sync', {
        deviceLabel: label,
        ops: batch.map((op) => ({
          opId: op.opId,
          kind: op.kind,
          heldOrderId: op.heldOrderId,
          baseVersion: op.baseVersion,
          updatedAt: op.updatedAt,
          payload: op.payload,
        })),
      });

      const settled: string[] = [];

      for (const item of response.result.results) {
        if (item.outcome === 'applied' || item.outcome === 'duplicate') {
          // The server holds it. Only now is it safe to forget.
          settled.push(item.opId);
          if (item.outcome === 'applied') totals.applied += 1;
          else totals.duplicates += 1;
          continue;
        }

        if (item.outcome === 'conflict') {
          totals.conflicts += 1;
          // KEPT, both versions, until a person chooses. Overwriting either
          // one is the thing this whole path exists to avoid.
          await updateHeldOp(item.opId, {
            syncStatus: 'failed',
            conflict:
              item.reason ??
              'This cart changed on the server while the till was offline.',
            failedReason: undefined,
            lastAttemptAt: new Date().toISOString(),
            attempts: (batch.find((op) => op.opId === item.opId)?.attempts ?? 0) + 1,
            serverVersion: item.serverVersion,
            serverStatus: item.serverStatus,
          });
          continue;
        }

        totals.failed += 1;
        await updateHeldOp(item.opId, {
          syncStatus: 'failed',
          failedReason: item.reason ?? 'The server refused this cart change.',
          lastAttemptAt: new Date().toISOString(),
          attempts: (batch.find((op) => op.opId === item.opId)?.attempts ?? 0) + 1,
        });
      }

      await forgetHeldOps(settled);
    }

    useOfflineStore.getState().setServerReachable(true);
    return totals;
  } catch {
    // The whole request failed - no connection, or the server is down.
    // NOTHING is marked as refused, because nothing was refused. The queue is
    // intact and the next attempt sends it again.
    return null;
  } finally {
    heldRunning = false;
    await refreshHeldQueue();
  }
}

/// Push the held-cart queue into the store so the screen can show it.
export async function refreshHeldQueue(): Promise<void> {
  if (!offlineStorageAvailable()) return;

  try {
    useOfflineStore.getState().setHeldOps(await listHeldOps());
  } catch {
    // A browser that cannot read its own store can still sell.
  }
}

/// Put a held-cart operation into the queue and update the screen.
export async function enqueueHeldOp(op: PendingHeldOp): Promise<void> {
  await queueHeldOp(op);
  await refreshHeldQueue();
}

/// Try one refused cart operation again, at a person's request.
export async function retryHeldOp(opId: string): Promise<void> {
  await updateHeldOp(opId, { syncStatus: 'pending', failedReason: undefined });
  await refreshHeldQueue();
}

/// Try one refused sale again, at a person's request.
///
/// Clears the recorded reason first, which is what makes it eligible for the
/// automatic path again.
export async function retrySale(offlineId: string): Promise<void> {
  await clearSaleFailure(offlineId);
  await refreshQueue();
  await syncNow({ manual: true });
}

/// Try every refused sale again.
export async function retryAllFailed(): Promise<void> {
  const queue = await listPendingSales();

  for (const sale of queue) {
    if (sale.failedReason !== undefined) await clearSaleFailure(sale.offlineId);
  }

  await refreshQueue();
  await syncNow({ manual: true });
}

// ---------------------------------------------------------------------------
// The cached catalogue
// ---------------------------------------------------------------------------

/// Capture what the till needs to keep selling.
///
/// Called while the connection is good, which is the only time it can be. A
/// failure here is not shown to the cashier: it means the till simply has an
/// older snapshot, and the screen already says how old.
export async function captureCatalogue(): Promise<boolean> {
  if (!offlineStorageAvailable()) return false;

  try {
    const response = await api.get<{ catalogue: CachedCatalogue }>('/sales/offline-catalogue');
    await saveCatalogue(response.catalogue);
    useOfflineStore.getState().setCatalogueCapturedAt(response.catalogue.capturedAt);
    useOfflineStore.getState().setServerReachable(true);
    // The catalogue is what the till SELLS from offline; the reserved
    // numbers are what it PRINTS under. Captured together, for the same
    // reason: only while the connection is good.
    void topUpReceiptReservations();
    return true;
  } catch {
    return false;
  }
}

/// Ask the server for this till's pool of receipt numbers, and keep the
/// answer. Silent on failure: a till with an empty pool still sells offline,
/// it just cannot print a receipt until the pool is filled.
export async function topUpReceiptReservations(): Promise<number> {
  if (!offlineStorageAvailable()) return 0;
  try {
    const device = await deviceId();
    const response = await api.post<{ receiptNumbers: string[] }>('/sales/receipt-reservations', {
      deviceId: device,
      want: RECEIPT_POOL,
    });
    await saveReceiptReservations(response.receiptNumbers);
    useOfflineStore.getState().setReservedReceipts(response.receiptNumbers.length);
    return response.receiptNumbers.length;
  } catch {
    return 0;
  }
}

/// Load whatever snapshot the till already has.
export async function loadCachedCatalogue(): Promise<CachedCatalogue | null> {
  if (!offlineStorageAvailable()) return null;

  try {
    const catalogue = await readCatalogue();
    if (catalogue) useOfflineStore.getState().setCatalogueCapturedAt(catalogue.capturedAt);
    return catalogue;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Is the server actually there?
// ---------------------------------------------------------------------------

/// How often to ask, and how patient one ask may be.
///
/// Twenty seconds is often enough that a cashier notices the badge change
/// within a sale, and rare enough that it is invisible on the network. Four
/// seconds of patience is longer than any healthy answer and shorter than a
/// cashier's attention.
const HEALTH_MS = 20_000;
const HEALTH_TIMEOUT_MS = 4_000;

/// How many probes in a row must fail before the badge says offline.
///
/// THIS IS THE FLICKER GUARD. A single dropped packet, a Wi-Fi roam between
/// two access points, or the server restarting after a deploy will fail one
/// probe and succeed on the next. Flipping the badge for that teaches the
/// cashier to ignore it, which is worse than not having it. Two failures at
/// twenty seconds apart means the badge is at most forty seconds behind the
/// truth, and it does not blink.
///
/// The threshold is deliberately ASYMMETRIC: coming back online is believed
/// immediately, because that report is backed by an answer from the server
/// and there is no cost to believing it early.
const MISSES_BEFORE_OFFLINE = 2;

/// Ask the server whether it is up, and record the answer.
///
/// Talks to `GET /api/health` directly rather than through `api.get`, for two
/// reasons: the endpoint is public, so it works while signed out and cannot
/// trip the session-expiry handling; and a probe must never be mistaken for
/// the user's own traffic in the error reporting.
///
/// Never throws. A probe that blew up would take the sweep with it.
export async function probeHealth(): Promise<boolean> {
  if (typeof fetch === 'undefined') return false;

  // Somebody is already asking. Wait for their answer rather than adding a
  // second request to a server that may be struggling.
  if (inFlight) return inFlight;

  inFlight = runProbe().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/// A probe that is guaranteed to be NEW.
///
/// `probeHealth` shares an answer that is already on its way, which is right
/// for the sweep and wrong for the `offline` event: the probe in flight was
/// asked before the event and its answer is discarded by the ticket window,
/// so sharing it would leave the till "unknown" until the next sweep, twenty
/// seconds away. This waits for whatever is in flight and then asks again.
function probeAfresh(): Promise<boolean> {
  const pending = inFlight ?? Promise.resolve(false);
  return pending.then(() => probeHealth(), () => probeHealth());
}

async function runProbe(): Promise<boolean> {
  const state = useOfflineStore.getState();
  // Taken at the START of the probe, so an answer is judged by when it was
  // asked for rather than by when it happened to come back.
  const ticket = claimTicket();
  const apply = (reachable: boolean) => applyReachable(ticket, reachable);

  // THE PROBE IS ALWAYS SENT, whatever the browser says about the network.
  //
  // This used to return `false` at once when `navigator.onLine` was false -
  // "no interface, nothing to probe". On the shop PC that was the wrong
  // conclusion, and it is why the till stopped selling and printing when the
  // internet went: Windows reports "no network" when the router or the line
  // is down, but the till's server is on THIS machine at 127.0.0.1 and the
  // receipt printer is on a USB cable next to it. The request to localhost
  // costs nothing and is the only thing that actually knows.
  //
  // What the browser's opinion still buys is IMPATIENCE: when it says offline
  // one failed probe is enough to believe it, instead of the usual two.
  //
  // Read from the STORE rather than from `navigator.onLine` directly. The
  // store is what the online/offline listeners maintain and what every screen
  // reads; a second, independent read of the same fact here is how two parts
  // of a system end up disagreeing about whether the till is connected.
  const browserSaysOffline = !state.browserOnline;

  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeout = controller
    ? setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS)
    : null;

  try {
    const response = await fetch('/api/health', {
      method: 'GET',
      // `no-store`, or a service worker or an HTTP cache will answer this
      // for the server and the till will believe a machine that is down.
      cache: 'no-store',
      credentials: 'same-origin',
      ...(controller ? { signal: controller.signal } : {}),
    });

    // 503 is the health endpoint saying the DATABASE is down. The process
    // answered, but the till cannot sell against it, so it counts as a miss.
    if (!response.ok) throw new Error(`health ${response.status}`);

    missedProbes = 0;
    apply(true);
    return true;
  } catch {
    missedProbes += 1;
    if (missedProbes >= MISSES_BEFORE_OFFLINE || browserSaysOffline) apply(false);
    return false;
  } finally {
    if (timeout) clearTimeout(timeout);
  }
}

/// Only for tests, which need the guards reset between cases.
///
/// The SEQUENCE is deliberately not reset. It is monotonic for the life of
/// the page and only ever compared against itself; zeroing it between cases
/// would make an old ticket look new, which is the exact confusion it exists
/// to prevent.
export function resetHealthProbeForTests(): void {
  missedProbes = 0;
  inFlight = null;
}

// ---------------------------------------------------------------------------
// When to try
// ---------------------------------------------------------------------------

/// How often to sweep while there is anything waiting.
const SWEEP_MS = 60_000;

/// Start listening for the connection coming back.
///
/// Safe to call more than once; only the first call attaches anything.
export function startSyncEngine(): void {
  if (listenersAttached || typeof window === 'undefined') return;
  listenersAttached = true;

  const store = useOfflineStore.getState();

  const onOnline = () => {
    useOfflineStore.getState().setBrowserOnline(true);
    // The event fires the instant the interface comes up, which is usually
    // before anything can actually route. A short pause avoids a guaranteed
    // failed request - and the probe, not the event, is what decides the
    // badge: an interface being up is not the server being reachable.
    setTimeout(() => {
      void (async () => {
        await probeHealth();
        await syncNow();
        await syncHeldOps();
      })();
    }, 1500);
  };

  const onOffline = () => {
    useOfflineStore.getState().setBrowserOnline(false);
    // "No network" is Windows' opinion of the internet, not proof the server
    // is gone - on the shop PC the server is on 127.0.0.1 and answers with
    // the router switched off. So the event does NOT declare the server
    // unreachable. It withdraws whatever was known (a fresh ticket, so a
    // probe that was already in flight cannot answer afterwards and be taken
    // for news) and asks the server at once. Until that answer arrives the
    // till behaves as offline - see `isEffectivelyOffline` - which on a
    // local server lasts milliseconds and on a cloud server is the truth.
    // A single failed probe is enough after this; the browser has said its
    // piece and one refusal from the network confirms it.
    missedProbes = MISSES_BEFORE_OFFLINE - 1;
    applyReachable(claimTicket(), null);
    void probeAfresh();
  };

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  store.setBrowserOnline(navigator.onLine);

  // The badge must be right before anything is queued, so the first answer is
  // asked for immediately rather than twenty seconds from now.
  void probeHealth();

  // And then steadily. This runs whether or not anything is waiting: the
  // question "can the till reach the server" has to be answerable on a quiet
  // afternoon, which is exactly when the sweep below has nothing to do.
  healthTimer = setInterval(() => {
    void probeHealth();
  }, HEALTH_MS);

  // A steady sweep as well as the event: `online` does not fire when the
  // machine had a connection all along and it was the SERVER that was down.
  timer = setInterval(() => {
    void (async () => {
      const waiting = await countPendingSales().catch(() => 0);
      if (waiting > 0) await syncNow();

      const carts = await countHeldOps().catch(() => 0);
      if (carts > 0) await syncHeldOps();
    })();
  }, SWEEP_MS);

  void refreshQueue();
  void refreshHeldQueue();
}

export function stopSyncEngine(): void {
  if (timer) clearInterval(timer);
  if (healthTimer) clearInterval(healthTimer);
  timer = null;
  healthTimer = null;
  listenersAttached = false;
}

/// Only for tests.
export function resetSyncEngineForTests(): void {
  running = false;
  heldRunning = false;
  missedProbes = 0;
  inFlight = null;
  stopSyncEngine();
}
